import React, { useState } from 'react'
import { Button, Form, Message } from 'semantic-ui-react'
import { updateAccount } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { parseSites } from 'utility/crm/template-suggestions'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmToast } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'

const label = { display: 'block', fontWeight: 600, margin: '10px 0 4px' }
const field = { width: '100%', padding: 8 }

const Body = ({ account, onSaved, onBusyChange }) => {
    const { lookups } = useCrmLookups()
    const { successToast } = useCrmToast()
    const [values, setValues] = useState({
        Name: account.Name || '',
        Industry: account.Industry || '',
        City: account.City || '',
        Website: account.Website || '',
        AccountType: account.AccountType || 'Prospect',
        OwnerUserId: account.OwnerUserId ?? '',
        SitesText: (account.Sites || []).join('\n'),
    })
    const [error, setError] = useState(null)
    const [touched, setTouched] = useState(false)
    const set = (key, value) => setValues(v => ({ ...v, [key]: value }))
    const nameMissing = !values.Name.trim()

    const submit = async event => {
        event.preventDefault()
        setTouched(true)
        if (nameMissing) return
        setError(null)
        onBusyChange(true)
        try {
            const saved = await updateAccount(account.AccountId, {
                Name: values.Name.trim(),
                Industry: values.Industry || undefined,
                City: values.City.trim() || undefined,
                Website: values.Website.trim() || undefined,
                AccountType: values.AccountType || undefined,
                OwnerUserId: values.OwnerUserId === '' ? undefined : Number(values.OwnerUserId),
                Sites: parseSites(values.SitesText),
            })
            successToast('Account updated')
            invalidateCrm(...ALL_CRM_SCOPES)
            onSaved(saved)
        } catch (err) {
            setError(toApiError(err).message)
        } finally {
            onBusyChange(false)
        }
    }

    return (
        <Form id="crm-account-form" noValidate onSubmit={submit}>
            {error ? (
                <Message negative role="alert">
                    {error}
                </Message>
            ) : null}
            <label htmlFor="acct-name" style={label}>
                Name *
            </label>
            <input id="acct-name" value={values.Name} onChange={e => set('Name', e.target.value)} style={field} maxLength={150} aria-invalid={touched && nameMissing} />
            {touched && nameMissing ? (
                <p role="alert" style={{ color: '#dc2626', margin: '2px 0', fontSize: '0.8rem' }}>
                    Name is required
                </p>
            ) : null}

            <label htmlFor="acct-industry" style={label}>
                Industry
            </label>
            <select id="acct-industry" value={values.Industry} onChange={e => set('Industry', e.target.value)} style={field}>
                <option value="">Not set</option>
                {(lookups?.Industries || []).map(i => (
                    <option key={i} value={i}>
                        {i}
                    </option>
                ))}
            </select>

            <label htmlFor="acct-city" style={label}>
                City
            </label>
            <input id="acct-city" value={values.City} onChange={e => set('City', e.target.value)} style={field} />

            <label htmlFor="acct-website" style={label}>
                Website
            </label>
            <input id="acct-website" value={values.Website} onChange={e => set('Website', e.target.value)} style={field} placeholder="example.com" />

            <label htmlFor="acct-type" style={label}>
                Type
            </label>
            <select id="acct-type" value={values.AccountType} onChange={e => set('AccountType', e.target.value)} style={field}>
                {(lookups?.AccountTypes || ['Prospect', 'Customer', 'Inactive']).map(t => (
                    <option key={t} value={t}>
                        {t}
                    </option>
                ))}
            </select>

            <label htmlFor="acct-owner" style={label}>
                Owner
            </label>
            <select id="acct-owner" value={values.OwnerUserId} onChange={e => set('OwnerUserId', e.target.value)} style={field}>
                <option value="">Unassigned</option>
                {(lookups?.Users || []).map(u => (
                    <option key={u.UserId} value={u.UserId}>
                        {u.FullName}
                    </option>
                ))}
            </select>

            <label htmlFor="acct-sites" style={label}>
                Sites / locations (one per line)
            </label>
            <textarea id="acct-sites" rows={4} value={values.SitesText} onChange={e => set('SitesText', e.target.value)} style={field} />
        </Form>
    )
}

/**
 * Edit account. (Accounts are normally created inline from the New contact dialog.)
 * @param {{ account: import('utility/crm/types').CrmAccount|null, onClose: () => void }} props
 */
export const AccountFormModal = ({ account, onClose }) => {
    const [busy, setBusy] = useState(false)
    return (
        <CrmModal
            open={Boolean(account)}
            onClose={busy ? () => {} : onClose}
            title="Edit account"
            width={560}
            footer={
                <>
                    <Button type="button" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="submit" form="crm-account-form" primary loading={busy} disabled={busy}>
                        Save changes
                    </Button>
                </>
            }
        >
            {account ? <Body key={account.AccountId} account={account} onBusyChange={setBusy} onSaved={onClose} /> : null}
        </CrmModal>
    )
}
